import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";

/**
 * Upsert Warehouse
 *
 * Takes ONE warehouse as single-value input sockets, shaped after the Gently
 * model (`im_warehouses`) — not after any source system. A CDC observer, a
 * curated mapping table, or a file parser maps its own columns into these
 * sockets, so the same step serves every source; anything source-specific
 * (field naming, hub flags, name disambiguation) belongs in that upstream
 * mapping.
 *
 * This is PASS ONE of warehouse ingest. Hierarchy edges cannot be written until
 * both of their endpoints exist, so `upsert_warehouse_hierarchy` must follow
 * this step to completion — two sequential fanouts, or two workflows.
 *
 * The conflict target is `(organization_id, origin_id)`: a warehouse code is
 * unique within an organization. `name` is a required socket and a plain display
 * attribute — it is NOT unique, because branch names legitimately repeat across
 * divisions, so nothing here matches on it. An unresolved `divisionCode` is
 * logged but does NOT skip the row — `division_id` is nullable and every consumer
 * treats it as an optional filter, so a warehouse with no division is still fully
 * usable for ingest.
 *
 * A row is skipped when its `warehouseCode` is empty or an Excel error token
 * (`#N/A`, `#REF!`, ...) — junk cells reach this step from the
 * spreadsheet-maintained mapping, and one was written as a real warehouse and
 * then resolved as a hierarchy PARENT on the next pass.
 *
 * `unresolvedDivision` is the third per-row output, and the only miss that
 * leaves a WRITTEN row: the warehouse lands with a null division. Without it in
 * the output, a run where every warehouse failed to resolve a division looks
 * identical to a perfect one. `failOnTotalDivisionMiss` (default on) turns the
 * all-rows case into a real failure — one miss is a bad mapping cell, all of
 * them is an environment with no division codes at all.
 *
 * Wiring `frameNumber` + `frameRowCount` from `stateful_table_read_frame` turns
 * on ORPHAN REPORTING: codes stored for this pipeline that the frame no longer
 * carries are named in the run output, with the frame number. Nothing is
 * deleted — a run against a stale frame once silently re-created a warehouse
 * that had just been re-keyed away, and the point is to make that visible.
 *
 * With a `batch` config the engine hands whole chunks to the bulk handler. A
 * leaf step: per-row outputs are execution results, not a downstream data
 * contract.
 */
export const upsertWarehouseStep = defineStepConfig({
  type: "upsert_warehouse",
  name: "Upsert Warehouse",
  description:
    "Upserts one warehouse (code, name, division code, hub/branch type, stock/sales location flags) into im_warehouses, keyed on (organization, origin id). Names may repeat across divisions; an unresolved division writes a null division; an empty or Excel-error warehouse code is skipped and reported. With the frame sockets wired, stored codes missing from the frame are reported too.",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    warehouseCode: z
      .string()
      .describe("Source warehouse code — stored as the warehouse's origin id"),
    name: z
      .string()
      .describe(
        "Display name. Not an identifier and not unique — branch names may repeat across divisions.",
      ),
    divisionCode: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Division code, matched against a division's internal_code / as400_code / gains_div_code / name. A code matching more than one division resolves to none.",
      ),
    type: z
      .string()
      .optional()
      .describe(
        "Warehouse type, e.g. `hub` or `branch`. Omitted keeps the stored value.",
      ),
    isStockLocation: z
      .boolean()
      .optional()
      .describe(
        "Whether the warehouse holds stock. Stored under metadata.is_stock_location; omitted keeps the stored value.",
      ),
    isSalesLocation: z
      .boolean()
      .optional()
      .describe(
        "Whether the warehouse sells. Stored under metadata.is_sales_location; omitted keeps the stored value.",
      ),
    frameNumber: z
      .number()
      .int()
      .nullable()
      .optional()
      .describe(
        "Frame the rows were read from. Reported in the run output, and required to enable orphan-code reporting. Wire from stateful_table_read_frame.frameNumber.",
      ),
    frameRowCount: z
      .number()
      .int()
      .nullable()
      .optional()
      .describe(
        "Row count of the WHOLE frame. Orphan reporting only runs when the batch provably covers the frame, so supply this (stateful_table_read_frame.rowCount) and keep batch.size at or above it.",
      ),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The warehouse was upserted"),
    skipped: z
      .boolean()
      .describe(
        "The row was skipped — today only for a junk warehouse code (empty or an Excel error token); the reason is logged, not emitted",
      ),
    unresolvedDivision: z
      .boolean()
      .describe(
        "A division code was supplied and did not resolve. The warehouse IS written, with a null division — this is the one miss that leaves a usable row, so it is emitted rather than only logged.",
      ),
  }),

  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    failOnTotalDivisionMiss: z
      .boolean()
      .prefault(true)
      .describe(
        "Fail the step when EVERY supplied division code fails to resolve. One miss is a bad mapping cell; all of them means the environment has no division codes at all, which writes every warehouse with a null division and reports success. Turn off only to seed warehouses deliberately before the divisions exist.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
